import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, Lock, BellRing, UserCircle, Sparkles,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { principalProfileApi } from '../api/services';
import type { PrincipalProfileEntry, PrincipalReminder } from '../types/models';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// PrincipalProfilePage — the EA's "what I know about my executive" surface.
// Eight section tabs (Personal / Family / Preferences / Travel / Stakeholders
// / Protocols / Dates / Health) containing category groups of inline-editable
// entries, plus an upcoming-dates reminder strip at the top.
// ---------------------------------------------------------------------------

const SECTIONS = ['Personal', 'Family', 'Preferences', 'Travel', 'Stakeholders', 'Protocols', 'Dates', 'Health'] as const;
type Section = typeof SECTIONS[number];

const SENSITIVITY_OPTIONS = ['Standard', 'Sensitive', 'HighlyConfidential'];

const SENSITIVITY_CHIP: Record<string, { className: string; label: string }> = {
  Standard: {
    className: 'bg-muted text-muted-foreground',
    label: 'Standard',
  },
  Sensitive: {
    className: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    label: 'Sensitive',
  },
  HighlyConfidential: {
    className: 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
    label: 'Highly Confidential',
  },
};

const SECTION_BAR: Record<Section, string> = {
  Personal: 'border-l-blue-400',
  Family: 'border-l-purple-400',
  Preferences: 'border-l-emerald-400',
  Travel: 'border-l-amber-400',
  Stakeholders: 'border-l-orange-400',
  Protocols: 'border-l-cyan-400',
  Dates: 'border-l-pink-400',
  Health: 'border-l-emerald-400',
};

const SECTION_ACCENT: Record<Section, string> = {
  Personal: 'text-blue-500',
  Family: 'text-purple-500',
  Preferences: 'text-emerald-500',
  Travel: 'text-amber-500',
  Stakeholders: 'text-orange-500',
  Protocols: 'text-cyan-500',
  Dates: 'text-pink-500',
  Health: 'text-emerald-500',
};

function formatDateCountdown(dateStr: string): string {
  try {
    const target = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    const diffMs = target.getTime() - today.getTime();
    const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days === -1) return 'Yesterday';
    if (days > 0) return `${days} days away`;
    return `${Math.abs(days)} days ago`;
  } catch {
    return dateStr;
  }
}

export default function PrincipalProfilePage() {
  const { user } = useAuth();
  const { execId: paramExecId } = useParams<{ execId?: string }>();
  const execId = paramExecId || user?.id || '';

  const [profile, setProfile] = useState<Record<string, PrincipalProfileEntry[]>>({});
  const [reminders, setReminders] = useState<PrincipalReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<Section>('Personal');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<Partial<PrincipalProfileEntry> | null>(null);
  const [editingInlineId, setEditingInlineId] = useState<string | null>(null);
  const [inlineValue, setInlineValue] = useState('');
  const [seeding, setSeeding] = useState(false);

  const isEmpty = Object.keys(profile).length === 0 ||
    Object.values(profile).every(entries => entries.length === 0);

  const loadProfile = useCallback(async () => {
    if (!execId) return;
    setLoading(true);
    setError(null);
    try {
      const [profileData, reminderData] = await Promise.all([
        principalProfileApi.getProfile(execId),
        principalProfileApi.getReminders(execId, 30),
      ]);
      setProfile(profileData);
      setReminders(reminderData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [execId]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleSeed = async () => {
    if (!window.confirm('Seed 68 template entries? This will create a starting template for the profile.')) return;
    setSeeding(true);
    try {
      await principalProfileApi.seedTemplate(execId);
      await loadProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to seed template');
    } finally {
      setSeeding(false);
    }
  };

  const handleSave = async () => {
    if (!editEntry?.label || !editEntry?.section || !editEntry?.category) return;
    try {
      if (editEntry.id) {
        await principalProfileApi.updateEntry(execId, editEntry.id, editEntry);
      } else {
        await principalProfileApi.addEntry(execId, editEntry);
      }
      setDialogOpen(false);
      setEditEntry(null);
      loadProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save entry');
    }
  };

  const handleDelete = async (entryId: string) => {
    if (!window.confirm('Delete this profile entry?')) return;
    try {
      await principalProfileApi.deleteEntry(execId, entryId);
      loadProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to delete');
    }
  };

  const handleInlineSave = async (entry: PrincipalProfileEntry) => {
    try {
      await principalProfileApi.updateEntry(execId, entry.id, { ...entry, value: inlineValue });
      setEditingInlineId(null);
      loadProfile();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update');
    }
  };

  const openAddDialog = (section: string, category: string) => {
    setEditEntry({
      section,
      category,
      label: '',
      value: '',
      notes: null,
      sensitivity: 'Standard',
      dateValue: null,
      isAnnualRecurrence: false,
      reminderDaysBefore: 7,
      displayOrder: 0,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (entry: PrincipalProfileEntry) => {
    setEditEntry({ ...entry });
    setDialogOpen(true);
  };

  const sectionEntries = profile[activeSection] || [];
  const categoriesInSection = sectionEntries.reduce<Record<string, PrincipalProfileEntry[]>>((acc, entry) => {
    if (!acc[entry.category]) acc[entry.category] = [];
    acc[entry.category].push(entry);
    return acc;
  }, {});
  Object.values(categoriesInSection).forEach(entries =>
    entries.sort((a, b) => a.displayOrder - b.displayOrder),
  );

  const totalEntries = Object.values(profile).reduce((sum, entries) => sum + (entries?.length ?? 0), 0);

  return (
    <div className="mx-auto max-w-[1280px] p-4 md:p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <UserCircle className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Principal Profile</h1>
            <p className="text-sm text-muted-foreground">
              Intelligence about your executive — {totalEntries} entries
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEmpty && !loading && (
            <Button
              variant="secondary"
              onClick={handleSeed}
              disabled={seeding}
              className="gap-1.5"
            >
              <Sparkles className="h-4 w-4" />
              {seeding ? 'Seeding...' : 'Seed Template'}
            </Button>
          )}
          <Button onClick={() => openAddDialog(activeSection, '')} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Add Entry
          </Button>
        </div>
      </div>

      {loading && (
        <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full w-1/3 animate-pulse bg-primary" />
        </div>
      )}
      {error && (
        <Alert variant="error" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Reminders */}
      {reminders.length > 0 && (
        <Card className="mb-6 border-l-[3px] border-l-pink-400">
          <CardContent className="py-3">
            <div className="mb-2 flex items-center gap-2">
              <BellRing className="h-5 w-5 text-pink-500" />
              <p className="text-sm font-semibold text-pink-600 dark:text-pink-400">
                Upcoming Dates — Next 30 Days
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {reminders.map((r) => {
                const tone = r.daysUntil <= 3
                  ? 'bg-rose-500/15 text-rose-600 dark:text-rose-400'
                  : r.daysUntil <= 7
                    ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                    : 'bg-blue-500/10 text-blue-700 dark:text-blue-400';
                return (
                  <span
                    key={r.id}
                    className={cn(
                      'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
                      tone,
                    )}
                  >
                    {r.label} — {r.daysUntil === 0 ? 'Today' : r.daysUntil === 1 ? 'Tomorrow' : `${r.daysUntil} days`}
                  </span>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section tabs */}
      <Tabs
        value={activeSection}
        onValueChange={(v) => setActiveSection(v as Section)}
        className="mb-6"
      >
        <TabsList className="overflow-x-auto">
          {SECTIONS.map((section) => {
            const count = (profile[section] || []).length;
            return (
              <TabsTrigger key={section} value={section}>
                {section} ({count})
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Empty state */}
      {!loading && Object.keys(categoriesInSection).length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="mb-3 text-sm text-muted-foreground">No entries in {activeSection}</p>
            <Button variant="outline" onClick={() => openAddDialog(activeSection, '')} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add First Entry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Category groups */}
      {Object.entries(categoriesInSection).map(([category, entries]) => (
        <div key={category} className="mb-6">
          <div className="mb-2 flex items-center justify-between">
            <p className={cn('text-sm font-semibold', SECTION_ACCENT[activeSection])}>{category}</p>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => openAddDialog(activeSection, category)}
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Add
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            {entries.map((entry) => {
              const sens = SENSITIVITY_CHIP[entry.sensitivity] || SENSITIVITY_CHIP.Standard;
              const editing = editingInlineId === entry.id;
              return (
                <Card key={entry.id} className={cn('h-full border-l-[3px]', SECTION_BAR[activeSection])}>
                  <CardContent className="py-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-center gap-1">
                          <p className="text-xs font-semibold text-muted-foreground">{entry.label}</p>
                          {entry.sensitivity === 'HighlyConfidential' && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Lock className="h-3.5 w-3.5 text-rose-500" />
                              </TooltipTrigger>
                              <TooltipContent>Highly Confidential</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        {editing ? (
                          <div className="flex items-center gap-1">
                            <Input
                              autoFocus
                              value={inlineValue}
                              onChange={(e) => setInlineValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleInlineSave(entry);
                                if (e.key === 'Escape') setEditingInlineId(null);
                              }}
                              className="h-8 flex-1"
                            />
                            <Button size="sm" onClick={() => handleInlineSave(entry)}>Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingInlineId(null)}>Cancel</Button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingInlineId(entry.id);
                              setInlineValue(entry.value);
                            }}
                            className="block text-left text-sm hover:text-primary"
                          >
                            {entry.value || <span className="text-muted-foreground">(click to set)</span>}
                          </button>
                        )}
                        {entry.dateValue && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {formatDateCountdown(entry.dateValue)}
                          </p>
                        )}
                        {entry.notes && (
                          <p className="mt-1 text-xs italic text-muted-foreground">{entry.notes}</p>
                        )}
                      </div>
                      <div className="ml-1 flex shrink-0 items-center gap-0.5">
                        <span
                          className={cn(
                            'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                            sens.className,
                          )}
                        >
                          {sens.label}
                        </span>
                        <Button size="icon-sm" variant="ghost" onClick={() => openEditDialog(entry)} aria-label="Edit">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="icon-sm" variant="ghost" onClick={() => handleDelete(entry.id)} aria-label="Delete">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      {/* Add / edit entry dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !o && setDialogOpen(false)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editEntry?.id ? 'Edit Entry' : 'New Entry'}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pp-section">Section</Label>
              <Select
                value={editEntry?.section || activeSection}
                onValueChange={(v) => setEditEntry(prev => ({ ...prev, section: v }))}
              >
                <SelectTrigger id="pp-section"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SECTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pp-category">Category</Label>
              <Input
                id="pp-category"
                value={editEntry?.category || ''}
                onChange={(e) => setEditEntry(prev => ({ ...prev, category: e.target.value }))}
                placeholder="e.g. Contact Details, Dietary Requirements"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pp-label">Label</Label>
              <Input
                id="pp-label"
                value={editEntry?.label || ''}
                onChange={(e) => setEditEntry(prev => ({ ...prev, label: e.target.value }))}
                placeholder="e.g. Mobile Number, Spouse Name"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pp-value">Value</Label>
              <Textarea
                id="pp-value"
                rows={2}
                value={editEntry?.value || ''}
                onChange={(e) => setEditEntry(prev => ({ ...prev, value: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pp-notes">Notes (optional)</Label>
              <Textarea
                id="pp-notes"
                rows={2}
                value={editEntry?.notes || ''}
                onChange={(e) => setEditEntry(prev => ({ ...prev, notes: e.target.value || null }))}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pp-sensitivity">Sensitivity</Label>
              <Select
                value={editEntry?.sensitivity || 'Standard'}
                onValueChange={(v) => setEditEntry(prev => ({ ...prev, sensitivity: v }))}
              >
                <SelectTrigger id="pp-sensitivity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SENSITIVITY_OPTIONS.map(s => (
                    <SelectItem key={s} value={s}>{SENSITIVITY_CHIP[s]?.label || s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="pp-date">Date Value (optional)</Label>
              <Input
                id="pp-date"
                type="date"
                value={editEntry?.dateValue || ''}
                onChange={(e) => setEditEntry(prev => ({ ...prev, dateValue: e.target.value || null }))}
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={editEntry?.isAnnualRecurrence || false}
                onCheckedChange={(c) => setEditEntry(prev => ({ ...prev, isAnnualRecurrence: !!c }))}
              />
              Annual recurrence (e.g. birthday)
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pp-reminder">Reminder Days Before</Label>
                <Input
                  id="pp-reminder"
                  type="number"
                  value={editEntry?.reminderDaysBefore ?? 7}
                  onChange={(e) => setEditEntry(prev => ({ ...prev, reminderDaysBefore: parseInt(e.target.value) || 0 }))}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="pp-display">Display Order</Label>
                <Input
                  id="pp-display"
                  type="number"
                  value={editEntry?.displayOrder ?? 0}
                  onChange={(e) => setEditEntry(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
